import { Theme, useTheme } from '@emotion/react';
import { format, parse } from 'date-fns';
import { isEmpty, isEqual } from 'lodash';
import React from 'react';

import { fontStyles } from '~src/theme/fontStyles';

import { ClockTime, OpeningHoursItem, OperationalHours, PickupLocation } from '../../api/api';
import { useCategoryState } from '../category/category-state';
import { TranslateICUFn, useTranslations } from '../commons/utils/translations/translate';
import { ArrowDownIcon } from '../icons/arrow-down-icon';
import { ArrowUpIcon } from '../icons/arrow-up-icon';
import { OperationalSectionComponent } from './operational-section';

type WeekdayType =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

const WEEKDAYS: WeekdayType[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/*
 * The old widget used the "formatTime" function to represent time in AM/PM
 * format, but the backend time format changed, hence new implementation.
 */
function formatClockTime(clockTime: ClockTime, tICU: TranslateICUFn) {
  const { hour, minute } = clockTime;
  try {
    const date = parse(`${hour}:${minute}`, 'H:m', new Date());
    const pattern =
      minute === 0
        ? tICU('widget.delivery_time.HOURS_FORMAT_ZERO_MINUTES')
        : tICU('widget.delivery_time.HOURS_FORMAT');
    return format(date, pattern);
  } catch (error) {
    console.error(error);
    return `${hour}:${minute}`;
  }
}

function formatOpeningHours(item: OpeningHoursItem, tICU: TranslateICUFn) {
  if (item.free_text) {
    return item.free_text;
  }
  if (!item.from || !item.to) {
    return '-';
  }
  return `${formatClockTime(item.from, tICU)} - ${formatClockTime(item.to, tICU)}`;
}

interface OperationalHoursBodyProps {
  tryShortVersion?: boolean;
  operationalHours: OperationalHours;
}

const OperationalHoursBody: React.FC<OperationalHoursBodyProps> = ({
  tryShortVersion,
  operationalHours,
}) => {
  const { tICU } = useTranslations();

  /*
   * The condition for displaying opening hours in shorted version is a bit
   * obscure, here is how it goes:
   * 1. Opening hours have to be the same for all days (that is the prerequisite).
   * 2. enable_additional_location_info category flag has to be set.
   * 3. Section info (additional info) has to be present.
   */
  if (tryShortVersion) {
    const canShowShortVersion = WEEKDAYS.every(
      weekday =>
        operationalHours[weekday] && isEqual(operationalHours[weekday], operationalHours['monday'])
    );
    if (canShowShortVersion) {
      const item = operationalHours['monday'];
      if (!item) {
        return null;
      }
      return (
        <div css={styles.row}>
          <div>{tICU('widget.SHIPPING_OPTIONS_MODAL.EQUAL_OPENING_HOURS_HEADER')}</div>
          <div>{formatOpeningHours(item, tICU)}</div>
        </div>
      );
    }
  }

  const rows = WEEKDAYS.map((weekday: WeekdayType) => {
    const item = operationalHours[weekday];
    if (!item) {
      return null;
    }
    return (
      <div key={weekday} css={styles.row} role="listitem">
        <div>{tICU(`widget.${weekday.slice(0, 3)}`)}</div>
        <div>{formatOpeningHours(item, tICU)}</div>
      </div>
    );
  });

  return <div role="list">{rows}</div>;
};

interface OperationalHoursComponentProps {
  location: PickupLocation;
}

export const OperationalHoursComponent: React.FC<OperationalHoursComponentProps> = ({
  location: locationProp,
}) => {
  const category = useCategoryState();
  const { tICU } = useTranslations();
  const theme = useTheme();

  const [expanded, setExpanded] = React.useState(false);

  if (!locationProp.operational_hours || isEmpty(locationProp.operational_hours)) {
    // operational_hours can be an empty object.
    return null;
  }

  const showIcaInfo = category.flags.enable_additional_location_info && !!locationProp.sections;

  const items = (
    <>
      <OperationalHoursBody
        tryShortVersion={showIcaInfo}
        operationalHours={locationProp.operational_hours}
      />
      {showIcaInfo &&
        locationProp.sections!.map(section => (
          <OperationalSectionComponent key={section.name} section={section} />
        ))}
    </>
  );

  return (
    <div css={styles.wrapper}>
      <button
        aria-expanded={expanded}
        css={styles.button}
        type="button"
        onClick={e => {
          e.stopPropagation();
          setExpanded(oldExpanded => !oldExpanded);
        }}
      >
        <span>{tICU('widget.SHIPPING_OPTIONS_MODAL.OPENING_HOURS')}</span>
        {expanded && <ArrowUpIcon color={theme.neutral35} />}
        {!expanded && <ArrowDownIcon color={theme.neutral35} />}
      </button>
      {expanded && items}
    </div>
  );
};

const styles = {
  wrapper: (theme: Theme) => ({
    display: 'grid',
    rowGap: '4px',
    color: theme.neutral35,
    ...fontStyles.px14_20,
  }),
  button: {
    height: '20px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '0',
    color: 'inherit',
    fontSize: 'inherit',
    display: 'flex',
    alignItems: 'center',
    ':focus-visible': {
      outline: '3px solid rgb(0, 160, 255)',
    },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: '24px',
  },
};
